import {
  EnvConfig,
  MediaStatus,
  useWorkspaceAsset,
  WorkspaceAsset,
} from '@gripp/shared-logic';
import type {
  ColumnData,
  ImageGalleryImage,
  SlideLayoutLocationState,
} from '@gripp/shared-ui';
import {
  ChangeAssetImage,
  EditIcon,
  ImageGallery,
  LoadingIndicator,
  SlideAction,
  SlideLayout,
  tokens,
  ViewMode,
} from '@gripp/shared-ui';
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { MediaPicker } from '../../shared/components/media/mediaPicker';
import { AssetRouteProvider } from '../assetRouteContext';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';

const IMAGE_POLL_INTERVAL_MS = 3000;
const IMAGE_POLL_MAX_ATTEMPTS = 20;

const ChangeImageHeaderButton = ({
  workspaceAsset,
  onChanged,
}: {
  workspaceAsset: WorkspaceAsset;
  onChanged: () => void;
}) => (
  <ChangeAssetImage
    workspaceAsset={workspaceAsset}
    mediaPicker={MediaPicker}
    onClose={onChanged}
    customTrigger={
      <View style={styles.changeButton} testID="asset-image-change-button">
        <EditIcon size={24} color={tokens.colors.slate[700]} />
      </View>
    }
  />
);

export const AssetImageView = () => {
  const { assetId = '', issueId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;

  const { workspaceAsset, invalidateQuery } = useWorkspaceAsset(assetId);
  const coverImage = workspaceAsset?.asset?.coverImage;
  const coverImageId = coverImage?.id;

  // Restart the processing poll whenever the cover image changes.
  const [pollAttempts, setPollAttempts] = useState(0);
  useEffect(() => {
    setPollAttempts(0);
  }, [coverImageId]);

  const isProcessing = coverImage?.status === MediaStatus.Pending;
  const isWaitingForImage =
    isProcessing && pollAttempts < IMAGE_POLL_MAX_ATTEMPTS;

  useEffect(() => {
    if (!isWaitingForImage) return;
    const timer = setTimeout(() => {
      invalidateQuery();
      setPollAttempts((count) => count + 1);
    }, IMAGE_POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [isWaitingForImage, pollAttempts, invalidateQuery]);

  if (!assetId) return null;

  const assetBasePath = issueId
    ? `/activities/${issueId}/asset/${assetId}`
    : `/assets/${assetId}`;

  const backToAsset = () =>
    navigate(assetBasePath, {
      state: { slideLayoutInitialTransition: SlideAction.PREV },
    });

  const galleryImages: ImageGalleryImage[] = coverImage
    ? [
        {
          id: coverImage.id,
          url: `${EnvConfig.mediaUrl}/images/${coverImage.id}/original.${coverImage.extension}`,
          name: coverImage.name,
        },
      ]
    : [];

  const leftColumn: ColumnData = {
    title: 'assets.menu.title',
    mainComponent: (
      <AssetRouteProvider
        value={{
          basePath: assetBasePath,
          listPath: issueId ? '/activities' : '/assets',
          isWorkspaceMode: false,
          issueId,
        }}
      >
        <AssetCard assetId={assetId} />
      </AssetRouteProvider>
    ),
    headerComponent: <AssetFilters assetId={assetId} />,
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    backButtonTransitionExitColumn: 'both',
    onBackButtonPress: backToAsset,
    disableScroll: false,
  };

  const rightColumn: ColumnData = {
    title: coverImage?.name ?? 'assets.menu.title',
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    onBackButtonPress: backToAsset,
    disableScroll: true,
    headerComponent: workspaceAsset ? (
      <ChangeImageHeaderButton
        workspaceAsset={workspaceAsset}
        onChanged={invalidateQuery}
      />
    ) : undefined,
    mainComponent: (
      <View style={styles.imageContainer} testID="asset-image-view">
        {isWaitingForImage ? (
          <LoadingIndicator flexContainer />
        ) : (
          galleryImages.length > 0 && (
            <ImageGallery
              images={galleryImages}
              initialIndex={0}
              showHeader={false}
              showCounter={false}
            />
          )
        )}
      </View>
    ),
  };

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};

const styles = StyleSheet.create({
  imageContainer: {
    flex: 1,
    backgroundColor: tokens.colors.neutral[100],
  },
  changeButton: {
    padding: 8,
    borderRadius: 4,
  },
});
